import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  X, Copy, Fingerprint, Clock, ChevronLeft, ChevronRight, Filter, CheckCircle2,
  Circle, Loader2, Wallet, Compass, GraduationCap, Video, Phone, Mail, UserPlus,
  AlertTriangle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { etStamp } from "@/lib/dates";
import OriginChip from "./OriginChip";
import { SortTH, applySort } from "./tableSort";

const PAGE_SIZE = 15;

// Column accessors for sortable headers.
const PROSPECT_SORT = {
  lead: (p) => (p.name || p.email || "").toLowerCase(),
  track: (p) => trackInfo(p.track).label,
  phone: (p) => p.phone || "",
  progress: (p) => p.progress || 0,
  status: (p) => p.status_label || "",
  // Registered-no-wallet first when sorted: those are the ones needing a wallet.
  account: (p) => (p.registered ? 0 : 1),
  first_seen: (p) => p.first_seen || "",
  sponsor: (p) => (p.sponsor_name || "~").toLowerCase(),
  came_from: (p) => (p.origin?.label || "~").toLowerCase(),
  leadlink: (p) => (p.carypact_url ? 0 : 1),
};

const TRACK = {
  discover: { label: "Discover", cls: "bg-violet-100 text-violet-700", expects: "Watch the sizzle, then the full presentation — no sign-up needed to look." },
  funnel: { label: "Funnel", cls: "bg-blue-100 text-blue-700", expects: "Create their account and complete the guided wallet setup." },
  sizzle: { label: "Sizzle → Funnel", cls: "bg-fuchsia-100 text-fuchsia-700", expects: "A quick sizzle intro, then straight into account setup." },
};
const trackInfo = (t) => TRACK[t] || TRACK.discover;

// Which videos each track actually shows the prospect.
// Funnel goes straight to account setup — no sizzle/presentation video.
const TRACK_VIDEOS = {
  discover: { sizzle: true, present: true },
  sizzle: { sizzle: true, present: false },
  funnel: { sizzle: false, present: false },
};
const trackVideos = (t) => TRACK_VIDEOS[t] || TRACK_VIDEOS.discover;

// Status badge colour by meaning — a finished prospect waiting on their wallet
// to sync stands out (amber), a bare click is muted, everything else neutral.
const statusCls = (label) => {
  const l = (label || "").toLowerCase();
  if (l.includes("ready to make member")) return "bg-emerald-600 text-white";
  if (l.includes("waiting for wallet")) return "bg-amber-100 text-amber-800";
  if (l.includes("clicked")) return "bg-slate-100 text-slate-500";
  if (l.includes("watched presentation")) return "bg-emerald-100 text-emerald-700";
  return "bg-black/5 text-ink-2";
};

// The CaryPact link the LEAD submitted during onboarding + its verification
// state. This is the gate for membership: present + verified in our tree +
// not already claimed by another account.
function LeadLinkCell({ p }) {
  if (!p.carypact_url) {
    return <span className="text-ink-3 text-[11px] italic" title="They haven't submitted their own CaryPact link yet">Not submitted</span>;
  }
  const short = String(p.carypact_url).replace(/^https?:\/\//, "").replace(/\/$/, "");
  let badge;
  if (p.wallet_taken && p.wallet_owner_self) {
    badge = <span className="text-[10px] font-bold text-emerald-700 inline-flex items-center gap-0.5"><CheckCircle2 size={9} /> Already a member</span>;
  } else if (p.wallet_taken) {
    badge = <span className="text-[10px] font-bold text-rose-700 inline-flex items-center gap-0.5"><AlertTriangle size={9} /> Duplicate — claimed by another</span>;
  } else if (p.wallet_in_tree) {
    badge = <span className="text-[10px] font-bold text-emerald-700 inline-flex items-center gap-0.5"><CheckCircle2 size={9} /> Verified in tree</span>;
  } else {
    badge = <span className="text-[10px] text-amber-700 inline-flex items-center gap-0.5"><Clock size={9} /> Not synced yet</span>;
  }
  return (
    <div className="flex flex-col gap-0.5" style={{ maxWidth: 170 }}>
      <a href={p.carypact_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
        className="text-[11px] font-mono text-indigo-700 hover:underline truncate" title={p.carypact_url}>{short}</a>
      {badge}
    </div>
  );
}

const DISCOVER_STATUS = {
  clicked: "Clicked link · no video yet",
  watching_sizzle: "Watching sizzle",
  info_submitted: "Info submitted",
  watching_present: "Watching presentation",
  watched_present: "Watched presentation",
  onboarding: "Started onboarding",
};

const SECTION_A = [
  { n: 1, title: "TokenPocket wallet", body: "Install the TokenPocket browser extension — this is their wallet." },
  { n: 2, title: "Back up 12-word phrase", body: "Create the wallet and write down the 12-word recovery phrase on paper." },
  { n: 3, title: "Click CaryPact invite link", body: "Open the sponsor's CaryPact invite link — this binds their wallet to the sponsor." },
  { n: 4, title: "Import USDT & CA tokens", body: "Add USDT and CA as custom tokens in TokenPocket." },
];
const SECTION_B = [
  { n: 1, title: "Fund wallet", body: "Send USDT + a little BNB (gas) into the new wallet on BNB Chain." },
  { n: 2, title: "Buy hashrate", body: "Buy their first $100 of hashrate on BOT Chain — earns CA every 24h." },
  { n: 3, title: "Stake CA", body: "Stake CA — daily auto-restake or 360-day locked." },
  { n: 4, title: "Copy CaryPact link", body: "Copy their own CaryPact invite link to connect their account." },
];

// What they told us they'd start with on the signup form.
const BUDGETS = { V0: "$100", V1: "$500", V2: "$2,500", V3: "$20K+" };

function ProgressBar({ pct, testid }) {
  const p = Math.max(0, Math.min(100, Math.round(pct || 0)));
  const color = p >= 95 ? "#00863A" : p >= 40 ? "#4060FF" : "#B45309";
  return (
    <div className="flex items-center gap-2 min-w-[110px]" data-testid={testid}>
      <div className="flex-1 h-2 rounded-full bg-black/5 overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${p}%`, background: color }} />
      </div>
      <span className="text-[11px] font-mono num w-9 text-right">{p}%</span>
    </div>
  );
}

// Merge discover watchers + onboarding trainees into one deduped list.
// A person shows up twice (the click, then the onboarding row) and each row
// remembers a different part of the trail — keep the most specific answer.
const ORIGIN_RANK = { campaign: 4, invite: 3, signup: 3, academy: 3, link: 2, admin: 1, none: 0 };
const bestOrigin = (...origins) =>
  origins.filter(Boolean).sort((a, b) => (ORIGIN_RANK[b.kind] ?? 0) - (ORIGIN_RANK[a.kind] ?? 0))[0] || null;

function buildProspects(inProgress, watching, allDiscover, registered) {
  const byEmail = new Map();
  (allDiscover || []).forEach((d) => { if (d.email) byEmail.set(d.email.toLowerCase(), d); });
  const traineeEmails = new Set();
  const out = [];

  (inProgress || []).forEach((r) => {
    const em = (r.email || "").toLowerCase();
    if (em) traineeEmails.add(em);
    const d = em ? byEmail.get(em) : null;
    let progress;
    if (r.flow === "academy_v1") {
      progress = Math.round(((r.academy_done || 0) / (r.academy_total || 12)) * 100);
    } else {
      const pre = Math.min(r.pre_step_index || 0, 5);
      const done = pre + (r.section_a || []).length + (r.section_b || []).length;
      progress = Math.round((done / 13) * 100);
    }
    // Ready to become a member = they submitted their OWN CaryPact link, that
    // wallet is VERIFIED in our synced tree, and it isn't already claimed by
    // another account. No submitted link → never ready, no matter what else.
    const ready = !!r.carypact_url && !!r.wallet_in_tree && !r.wallet_taken;
    out.push({
      key: `setup-${r.id}`, type: "setup", id: r.id, flow: r.flow,
      name: r.name, email: r.email, phone: r.phone || d?.phone,
      track: d?.track || "funnel",
      sizzle_pct: d ? (d.sizzle_pct || 0) : null,
      present_pct: d ? (d.present_pct || 0) : null,
      progress,
      ready,
      own_wallet: r.wallet || null,
      carypact_url: r.carypact_url || null,
      wallet_in_tree: !!r.wallet_in_tree,
      wallet_taken: !!r.wallet_taken,
      wallet_owner_self: !!r.wallet_owner_self,
      status_label: ready ? "Ready to make member" : (r.status_label || "Setting up"),
      first_seen: d?.created_at || r.created_at || r.started_at,
      sponsor_name: d?.sponsor_name || r.sponsor_name,
      link_code: d?.link_code || r.ref,
      link_suffix: d?.link_suffix,
      wallet: d?.sponsor_wallet_name,
      pre_step_index: r.pre_step_index || 0,
      section_a: new Set(r.section_a || []),
      section_b: new Set(r.section_b || []),
      academy_done: r.academy_done, academy_total: r.academy_total,
      invited_via: d?.invited_via,
      budget_tier: d?.budget_tier || r.budget_tier || null,
      introduced_by: d?.introduced_by || r.introduced_by || null,
      // A campaign wins wherever we captured it — the click that started the
      // journey usually lands on the discover row, the trainee row later.
      origin: bestOrigin(d?.origin, r.origin),
    });
  });

  (watching || []).forEach((l) => {
    const em = (l.email || "").toLowerCase();
    if (em && traineeEmails.has(em)) return;
    // Discover watchers never submitted their own CaryPact link, so they can
    // never be "ready to make a member" — a wallet-email match isn't enough.
    const ready = false;
    out.push({
      key: `disc-${l.id}`, type: "discover", id: l.id, flow: "discover",
      name: l.name, email: l.email, phone: l.phone,
      track: l.track || "discover",
      sizzle_pct: l.sizzle_pct || 0, present_pct: l.present_pct || 0,
      progress: Math.round(((l.sizzle_pct || 0) + (l.present_pct || 0)) / 2),
      ready,
      own_wallet: null,
      carypact_url: null,
      wallet_in_tree: false,
      wallet_taken: false,
      status_label: DISCOVER_STATUS[l.status] || "Watching",
      first_seen: l.created_at,
      sponsor_name: l.sponsor_name, link_code: l.link_code, link_suffix: l.link_suffix,
      wallet: l.sponsor_wallet_name,
      pre_step_index: 0, section_a: new Set(), section_b: new Set(),
      invited_via: l.invited_via,
      budget_tier: l.budget_tier || null,
      introduced_by: l.introduced_by || null,
      origin: l.origin || null,
    });
  });

  // People who signed up on the registration page. They own no wallet, so they
  // are not members and cannot sign in — but they belong in this one table like
  // everybody else, marked in the Account column rather than piled into a second
  // table above it. If they're already here as a trainee or a watcher, mark that
  // row instead of adding a duplicate.
  const byKeyEmail = new Map();
  out.forEach((r) => { if (r.email) byKeyEmail.set(r.email.toLowerCase(), r); });
  (registered || []).forEach((u) => {
    const em = (u.email || "").toLowerCase();
    const existing = em ? byKeyEmail.get(em) : null;
    if (existing) {
      existing.registered = true;
      existing.registered_at = u.created_at;
      existing.login_id = u.id;
      return;
    }
    out.push({
      key: `reg-${u.id}`, type: "registered", id: u.id, login_id: u.id, flow: "registered",
      name: u.name, email: u.email, phone: u.phone,
      track: u.came_from?.kind === "discover" ? "discover" : "funnel",
      sizzle_pct: null, present_pct: null, progress: 0,
      ready: false, registered: true, registered_at: u.created_at,
      own_wallet: null, carypact_url: null, wallet_in_tree: false, wallet_taken: false,
      status_label: "Registered · no wallet",
      first_seen: u.created_at,
      sponsor_name: u.sponsor_name || u.came_from?.sponsor_name,
      link_code: u.came_from?.code, link_suffix: null,
      wallet: u.came_from?.wallet,
      pre_step_index: 0, section_a: new Set(), section_b: new Set(),
      invited_via: null, budget_tier: null, introduced_by: null,
      origin: u.origin || null,
    });
  });

  out.sort((a, b) =>
    (b.ready ? 1 : 0) - (a.ready ? 1 : 0)
    || (b.first_seen || "").localeCompare(a.first_seen || ""));
  return out;
}

const StepRow = ({ done, active, title, body, testid }) => (
  <div className="flex items-start gap-2.5" data-testid={testid}>
    {done
      ? <CheckCircle2 size={16} className="text-emerald-600 mt-0.5 shrink-0" />
      : active
        ? <Loader2 size={16} className="text-blue-600 mt-0.5 shrink-0" />
        : <Circle size={16} className="text-ink-3/40 mt-0.5 shrink-0" />}
    <div>
      <div className={`text-sm font-medium ${done ? "text-slate-900" : active ? "text-blue-700" : "text-ink-3"}`}>
        {title} {active && <span className="text-[10px] font-bold uppercase tracking-wider">· in progress</span>}
      </div>
      <div className="text-[12px] text-ink-3 leading-snug">{body}</div>
    </div>
  </div>
);

function DetailModal({ p, copy, onClose, onMakeMember }) {
  const ti = trackInfo(p.track);
  const sponsorLink = p.link_code
    ? `${window.location.origin}/discover?ref=${p.link_code}${p.link_suffix ? `&s=${p.link_suffix}` : ""}`
    : "";
  const hasVideo = p.sizzle_pct !== null && p.sizzle_pct !== undefined;
  const preDone = (p.pre_step_index || 0) >= 5;
  const preActive = !preDone && (p.pre_step_index || 0) > 0;
  const startedSetup = p.type === "setup";

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 md:p-8 overflow-y-auto"
      style={{ background: "rgba(15,18,38,0.45)", backdropFilter: "blur(4px)" }}
      onClick={onClose} data-testid="prospect-detail-modal">
      <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl my-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-black/5 flex items-start justify-between gap-4">
          <div>
            <div className="text-lg font-semibold text-slate-900">
              {p.name || (p.email ? <span className="font-mono text-base">{p.email}</span> : <span className="text-ink-3 italic">anonymous</span>)}
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-1.5">
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${ti.cls}`} data-testid="modal-track">{ti.label}</span>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${statusCls(p.status_label)}`}>{p.status_label}</span>
              {p.invited_via === "email" && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-50 text-blue-700">emailed</span>}
            </div>
          </div>
          <button onClick={onClose} data-testid="prospect-modal-close" className="text-ink-3 hover:text-ink-1 p-1"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-5">
          <div className="text-sm text-ink-2 bg-black/[0.03] rounded-xl px-4 py-3">
            <span className="font-semibold text-ink-1">Should be doing: </span>{ti.expects}
          </div>

          {/* Contact + attribution */}
          <div className="grid sm:grid-cols-2 gap-3 text-sm">
            <div className="space-y-1.5">
              {p.email && <div className="flex items-center gap-2"><Mail size={13} className="text-ink-3" /><span className="font-mono text-[13px]">{p.email}</span><button onClick={() => copy(p.email)} className="text-ink-3 hover:text-ink-1"><Copy size={12} /></button></div>}
              {p.phone && <div className="flex items-center gap-2"><Phone size={13} className="text-ink-3" /> {p.phone}</div>}
              <div className="flex items-center gap-2 text-[13px] text-ink-3"><Clock size={13} /> First seen {p.first_seen ? `${etStamp(p.first_seen)} ET` : "—"}</div>
              {p.budget_tier && (
                <div className="text-[13px]" data-testid="prospect-modal-budget">
                  <span className="text-ink-3">Starting budget: </span>
                  <b>{BUDGETS[p.budget_tier] || p.budget_tier}</b> <span className="text-ink-3">({p.budget_tier}, their own pick)</span>
                </div>
              )}
            </div>
            <div className="space-y-1.5 text-[13px]">
              <div className="flex items-start gap-2">
                <span className="text-ink-3">Came from: </span>
                <OriginChip origin={p.origin} testid="prospect-modal-origin" />
              </div>
              {p.origin?.detail && <div className="text-[12px] text-ink-3">{p.origin.detail}</div>}
              <div><span className="text-ink-3">Sponsor: </span>{p.sponsor_name || <span className="text-amber-700">direct</span>}</div>
              {p.introduced_by && (
                <div data-testid="prospect-modal-introduced-by">
                  <span className="text-ink-3">They said they were introduced by: </span>
                  <b>{p.introduced_by}</b> <span className="text-ink-3">(typed on the signup form)</span>
                </div>
              )}
              {p.wallet && <div><span className="text-ink-3">Wallet: </span>{p.wallet}</div>}
              {p.link_code && (
                <button onClick={() => copy(sponsorLink)} className="inline-flex items-center gap-1.5 font-mono text-[12px] text-blue-700 hover:underline" title={sponsorLink}>
                  ?ref={p.link_code}{p.link_suffix ? `\u00b7${p.link_suffix}` : ""} <Copy size={11} />
                </button>
              )}
            </div>
          </div>

          {/* Video funnel */}
          {(() => {
            const tv = trackVideos(p.track);
            const anyVideo = tv.sizzle || tv.present;
            return (
              <div>
                <div className="text-[11px] uppercase tracking-widest text-ink-3 mb-2 flex items-center gap-1.5"><Video size={13} /> Videos</div>
                {!anyVideo ? (
                  <div className="text-[13px] text-ink-3">The Funnel track goes straight to account setup — no sizzle or presentation video.</div>
                ) : !hasVideo ? (
                  <div className="text-[13px] text-ink-3">No video activity recorded yet.</div>
                ) : (
                  <div className="space-y-2">
                    {tv.sizzle && <div className="flex items-center gap-3"><span className="text-sm w-28 shrink-0">Sizzle video</span><ProgressBar pct={p.sizzle_pct} /></div>}
                    {tv.present && <div className="flex items-center gap-3"><span className="text-sm w-28 shrink-0">Presentation video</span><ProgressBar pct={p.present_pct} /></div>}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Setup steps */}
          {p.flow === "academy_v1" ? (
            <div>
              <div className="text-[11px] uppercase tracking-widest text-ink-3 mb-2 flex items-center gap-1.5"><GraduationCap size={13} /> Academy (legacy)</div>
              <div className="text-sm">Lesson {p.academy_done || 0} of {p.academy_total || 12} complete.</div>
            </div>
          ) : (
            <div>
              <div className="text-[11px] uppercase tracking-widest text-ink-3 mb-3 flex items-center gap-1.5"><GraduationCap size={13} /> Account setup</div>
              {!startedSetup && <div className="text-[13px] text-ink-3 mb-3">Hasn&apos;t started account setup yet — here&apos;s what&apos;s ahead.</div>}
              <div className="space-y-4">
                <StepRow done={preDone} active={preActive} title="Intro & readiness"
                  body="Watched the intro, agreed to the disclaimer, confirmed ready, set their location." testid="prospect-step-intro" />
                <div>
                  <div className="text-[11px] font-bold text-ink-2 mb-2">Section A · Wallet setup</div>
                  <div className="space-y-3 pl-1">
                    {SECTION_A.map((s) => (
                      <StepRow key={`a${s.n}`} done={p.section_a.has(s.n)} title={s.title} body={s.body} testid={`prospect-step-a${s.n}`} />
                    ))}
                  </div>
                </div>
                <div>
                  <div className="text-[11px] font-bold text-ink-2 mb-2">Section B · Fund &amp; earn</div>
                  <div className="space-y-3 pl-1">
                    {SECTION_B.map((s) => (
                      <StepRow key={`b${s.n}`} done={p.section_b.has(s.n)} title={s.title} body={s.body} testid={`prospect-step-b${s.n}`} />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-black/5 flex items-center justify-between">
          {p.email
            ? <Link to={`/admin/trace?email=${encodeURIComponent(p.email)}`} data-testid="prospect-modal-trace" className="text-sm text-ink-2 hover:text-ink-1 inline-flex items-center gap-1.5"><Fingerprint size={14} /> Full journey</Link>
            : <span />}
          <div className="flex items-center gap-2">
            {p.ready && onMakeMember && (
              <Button size="sm" onClick={() => onMakeMember(p)} data-testid="prospect-modal-make-member"
                className="bg-emerald-600 hover:bg-emerald-700 text-white">
                <UserPlus size={14} className="mr-1.5" /> Make member in Wallets
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={onClose}>Close</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ProspectsPanel({ inProgress, watching, allDiscover, registered, copy, onMakeMember, onReject, busyId }) {
  const [track, setTrack] = useState("all");
  const [type, setType] = useState("all");
  const [sponsor, setSponsor] = useState("all");
  const [status, setStatus] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [sort, setSort] = useState({ key: null, dir: "asc" });
  const [page, setPage] = useState(1);
  const [openKey, setOpenKey] = useState(null);

  const all = useMemo(() => buildProspects(inProgress, watching, allDiscover, registered), [inProgress, watching, allDiscover, registered]);
  const sponsorOpts = useMemo(() => Array.from(new Set(all.map((p) => p.sponsor_name).filter(Boolean))).sort(), [all]);
  const statusOpts = useMemo(() => Array.from(new Set(all.map((p) => p.status_label).filter(Boolean))).sort(), [all]);

  const filtered = useMemo(() => {
    return all.filter((p) => {
      if (track !== "all" && p.track !== track) return false;
      if (type === "ready") { if (!p.ready) return false; }
      else if (type !== "all" && p.type !== type) return false;
      if (sponsor !== "all" && (p.sponsor_name || "") !== sponsor) return false;
      if (status !== "all" && (p.status_label || "") !== status) return false;
      if (from && (p.first_seen || "") < from) return false;
      if (to && (p.first_seen || "") > `${to}T23:59:59`) return false;
      return true;
    });
  }, [all, track, type, sponsor, status, from, to]);

  const sorted = useMemo(() => applySort(filtered, sort, PROSPECT_SORT), [filtered, sort]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const curPage = Math.min(page, totalPages);
  const rows = sorted.slice((curPage - 1) * PAGE_SIZE, curPage * PAGE_SIZE);
  const open = all.find((p) => p.key === openKey) || null;

  useEffect(() => { setPage(1); }, [track, type, sponsor, status, from, to, sort]);
  const anyFilter = track !== "all" || type !== "all" || sponsor !== "all" || status !== "all" || from || to;

  return (
    <div data-testid="prospects-panel">
      {/* Filters */}
      <div className="glass p-3 mb-4 flex flex-wrap items-center gap-2" data-testid="prospects-filters">
        <span className="text-ink-3 inline-flex items-center gap-1.5 text-xs font-semibold pl-1"><Filter size={13} /> Filter</span>
        <select value={track} onChange={(e) => setTrack(e.target.value)} data-testid="prospect-filter-track"
          className="text-xs rounded-lg border border-black/10 bg-white px-2.5 py-1.5">
          <option value="all">All tracks</option>
          <option value="discover">Discover</option>
          <option value="funnel">Funnel</option>
          <option value="sizzle">Sizzle → Funnel</option>
        </select>
        <select value={type} onChange={(e) => setType(e.target.value)} data-testid="prospect-filter-type"
          className="text-xs rounded-lg border border-black/10 bg-white px-2.5 py-1.5">
          <option value="all">Everyone</option>
          <option value="ready">Ready to make member</option>
          <option value="discover">Watching videos</option>
          <option value="setup">Setting up account</option>
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="prospect-filter-status"
          className="text-xs rounded-lg border border-black/10 bg-white px-2.5 py-1.5">
          <option value="all">Any status</option>
          {statusOpts.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={sponsor} onChange={(e) => setSponsor(e.target.value)} data-testid="prospect-filter-sponsor"
          className="text-xs rounded-lg border border-black/10 bg-white px-2.5 py-1.5">
          <option value="all">Any sponsor</option>
          {sponsorOpts.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <label className="text-xs text-ink-3 inline-flex items-center gap-1.5">From
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="prospect-filter-from"
            className="rounded-lg border border-black/10 bg-white px-2 py-1" />
        </label>
        <label className="text-xs text-ink-3 inline-flex items-center gap-1.5">To
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="prospect-filter-to"
            className="rounded-lg border border-black/10 bg-white px-2 py-1" />
        </label>
        {anyFilter && (
          <button onClick={() => { setTrack("all"); setType("all"); setSponsor("all"); setStatus("all"); setFrom(""); setTo(""); }}
            data-testid="prospect-filter-clear" className="text-xs text-blue-700 hover:underline ml-auto">Clear</button>
        )}
        <span className={`text-xs text-ink-3 ${anyFilter ? "" : "ml-auto"}`} data-testid="prospects-count">{filtered.length} {filtered.length === 1 ? "person" : "people"}</span>
      </div>

      {/* Table */}
      <div className="overflow-x-auto glass" style={{ borderRadius: 16 }}>
        <table className="modern prospects-table w-full text-sm">
          <thead>
            <tr>
              <SortTH label="Lead" k="lead" sort={sort} setSort={setSort} />
              <SortTH label="Track" k="track" sort={sort} setSort={setSort} />
              <SortTH label="Phone" k="phone" sort={sort} setSort={setSort} />
              <SortTH label="Progress" k="progress" sort={sort} setSort={setSort} />
              <SortTH label="Status" k="status" sort={sort} setSort={setSort} />
              <SortTH label="Account" k="account" sort={sort} setSort={setSort} />
              <SortTH label="First seen · ET" k="first_seen" sort={sort} setSort={setSort} />
              <SortTH label="Sponsor" k="sponsor" sort={sort} setSort={setSort} />
              <SortTH label="Came from" k="came_from" sort={sort} setSort={setSort} />
              <SortTH label="Lead's CaryPact link" k="leadlink" sort={sort} setSort={setSort} />
              <th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan="11" className="text-center py-10 text-ink-3" data-testid="prospects-empty">No prospects match these filters.</td></tr>
            ) : rows.map((p) => {
              const ti = trackInfo(p.track);
              return (
                <tr key={p.key} className="group cursor-pointer" onClick={() => setOpenKey(p.key)} data-testid={`prospect-row-${p.key}`}>
                  <td>
                    <div className="font-medium text-xs flex items-center gap-1.5 flex-wrap">
                      {p.invited_via === "email" && <span className="inline-block px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-blue-50 text-blue-700">emailed</span>}
                      <span>{p.name || (p.email ? <span className="font-mono">{p.email}</span> : <span className="text-ink-3 italic">anonymous</span>)}</span>
                      {p.budget_tier && (
                        <span className="inline-block px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-violet-50 text-violet-700"
                          title={`Budget they picked on the signup form: ${BUDGETS[p.budget_tier] || p.budget_tier}`}
                          data-testid={`prospect-budget-${p.key}`}>
                          {p.budget_tier} · {BUDGETS[p.budget_tier] || "—"}
                        </span>
                      )}
                    </div>
                    {p.name && p.email && <div className="text-[11px] text-ink-3 font-mono">{p.email}</div>}
                  </td>
                  <td><span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${ti.cls}`} data-testid={`prospect-track-${p.key}`}>{ti.label}</span></td>
                  <td className="text-[11px] font-mono">{p.phone || "—"}</td>
                  <td><ProgressBar pct={p.progress} testid={`prospect-progress-${p.key}`} /></td>
                  <td><span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${statusCls(p.status_label)}`}>{p.status_label}</span></td>
                  <td className="text-[11px] whitespace-nowrap" data-testid={`prospect-account-${p.key}`}>
                    {p.registered ? (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold"
                        style={{ background: "rgba(180,83,9,0.12)", color: "#B45309" }}
                        title="They signed up on the registration page. No wallet, so they aren't a member and can't sign in yet — attach their wallet in Wallets and click the person icon on that row.">
                        No wallet · can&apos;t sign in
                      </span>
                    ) : <span className="text-ink-3">—</span>}
                  </td>
                  <td className="text-[11px] whitespace-nowrap">{p.first_seen ? etStamp(p.first_seen) : "—"}</td>
                  <td className="text-[11px]" data-testid={`prospect-sponsor-${p.key}`}>
                    {p.sponsor_name
                      || (p.introduced_by
                        ? <span title="Self-reported on the signup form — they arrived without a referral link">{p.introduced_by} <span className="text-ink-3">· says</span></span>
                        : <span className="text-ink-3">direct</span>)}
                  </td>
                  <td className="text-[11px]" data-testid={`prospect-came-from-${p.key}`}>
                    <OriginChip origin={p.origin} testid={`origin-chip-${p.key}`} />
                    {p.link_code && (
                      <div className="font-mono text-[10px] text-ink-3 truncate max-w-[150px]"
                        title={`Wallet link on the click: ${p.link_code}${p.link_suffix ? `·${p.link_suffix}` : ""}`}>
                        {p.link_code}{p.link_suffix ? `·${p.link_suffix}` : ""}
                      </div>
                    )}
                  </td>
                  <td data-testid={`prospect-lead-link-${p.key}`}><LeadLinkCell p={p} /></td>
                  <td className="text-right" onClick={(e) => e.stopPropagation()}>
                    {p.registered && !p.ready ? (
                      <span className="inline-flex items-center gap-1">
                        <button onClick={() => onMakeMember && onMakeMember(p)} data-testid={`prospect-find-wallet-${p.key}`}
                          title="Open Wallets filtered to their email so you can attach their wallet, then click the person icon on that row"
                          className="px-2 py-1 rounded-lg text-[10px] font-bold border border-black/10 bg-white hover:border-black/25">
                          Find their wallet
                        </button>
                        {onReject && (
                          <button onClick={() => onReject({ id: p.login_id, name: p.name, email: p.email })}
                            disabled={busyId === p.login_id} data-testid={`prospect-not-real-${p.key}`}
                            title="Not a real sign-up — deactivate it"
                            className="px-2 py-1 rounded-lg text-[10px] font-semibold text-ink-3 hover:text-ink-1">
                            Not real
                          </button>
                        )}
                      </span>
                    ) : p.ready ? (
                      <button onClick={() => onMakeMember && onMakeMember(p)} data-testid={`prospect-make-member-${p.key}`}
                        title="They've watched everything, finished onboarding and their wallet has synced — open Wallets to make them a member"
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold bg-emerald-600 text-white hover:bg-emerald-700 transition-colors">
                        <UserPlus size={11} /> Make member
                      </button>
                    ) : (p.status_label || "").toLowerCase().includes("waiting for wallet") ? (
                      <span className="inline-flex items-center gap-1 text-[10px] text-amber-700" title="Finished — waiting for their wallet to sync into your tree" data-testid={`prospect-waiting-${p.key}`}>
                        <Clock size={11} /> Waiting for wallet
                      </span>
                    ) : (
                      <span className="text-ink-3 text-[11px]">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3 mt-4" data-testid="prospects-pagination">
          <Button size="sm" variant="outline" disabled={curPage <= 1} onClick={() => setPage(curPage - 1)} data-testid="prospects-prev"><ChevronLeft size={14} /> Prev</Button>
          <span className="text-xs text-ink-3">Page {curPage} of {totalPages}</span>
          <Button size="sm" variant="outline" disabled={curPage >= totalPages} onClick={() => setPage(curPage + 1)} data-testid="prospects-next">Next <ChevronRight size={14} /></Button>
        </div>
      )}

      {open && <DetailModal p={open} copy={copy} onClose={() => setOpenKey(null)} onMakeMember={onMakeMember} />}
    </div>
  );
}
